import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { IonContent, IonInput, IonButton, IonPage, IonGrid, IonRow, IonCol, IonLoading, useIonAlert, IonToolbar, IonTitle, IonHeader } from '@ionic/react';
import { getUserIdentityProvider } from "../api/IAM";
import { _iamKey, setAuthTypePreference, getAuthTypePreference } from '../util/Preferences';
import { TranslationMessagesContext, TranslationsContext } from "../util/Translations";
import { logCustomMessage } from "../util/logger";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { getBiometricCredential, isBiometricAvailable, isBiometricEnabled, promptBiometric, setBiometricEnabled } from "../util/Biometric";
import { clearCachedCredentialIfNotEnrolled, isPortalTokenValid, restoreSessionFromBiometric, validateBearerToken } from "../util/OAuth";

const EmailForm: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [showLoading, setShowLoading] = useState(false);
  const [showBiometric, setShowBiometric] = useState(false);
  const [bioLabel, setBioLabel] = useState('Biometric');
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { translations } = useContext(TranslationsContext);
  const [presentAlert] = useIonAlert();
  const history = useHistory();

  useEffect(() => {
    checkIAMQueryParam();
    checkIAMCacheExists();
  }, []);

  async function hashEmail(email: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(email);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setShowLoading(true);
    let response = await getUserIdentityProvider(email);
    const hashedEmail = await hashEmail(email);
    logCustomMessage(
      "Get User Identity Provider",
      SeverityLevel.Information,
      `Settings/GetUserIdentityProvider/${hashedEmail}`,
      { email: hashedEmail },
      response
    );
    if (!response.data?.success) {
      setShowLoading(false);
      const strings: string[] = response.data?.messageList?.map(
        (message: { text: string }) => {
          return message.text;
        }
      );
      presentAlert({
        message: strings.join(" "),
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK"
          },
        ],
      });
      return;
    }
    let iamType = response?.data?.user?.iamProvider === 1 ? 'ApteanIAM' : 'B2C';
    let domainHint = response.data?.identityProvider;
    //This will handle taking the user through B2C/IAM authentication.
    setAuthTypePreference(_iamKey, iamType);
    sessionStorage.setItem('UserLoginEmail', email);
    history.push(`/AuthCallback?email=${encodeURIComponent(email)}&domainhint=${domainHint}`);
  };

  const checkIAMQueryParam = () => {
    let url = new URL(window.location.href),
      searchParams = new URLSearchParams(url.search),
      iamParam = searchParams.get('IAM');

    if (iamParam) {
      setAuthTypePreference(_iamKey, iamParam);
      // Bug #879938: mark this as a Tenant Portal launch so the portal sign-in
      // forces a fresh B2C authentication instead of silently reusing a previous
      // user's SSO session. Set synchronously (sessionStorage) so it survives the
      // redirect and cannot be lost to the checkIAMCacheExists race below.
      sessionStorage.setItem('portalLaunch', 'true');
      window.location.href = "/AuthCallback";
    }
  };

  const checkIAMCacheExists = () => {
    getAuthTypePreference(_iamKey).then(async iamType => {
      let iamValue = iamType?.value;
      if (!iamValue) return;
      // If a valid session still exists (app was just closed/backgrounded, NOT
      // logged out), continue straight into the app - no biometric prompt.
      // Require BOTH the EAM token and the portal token to be valid; if either
      // has expired, fall through and offer biometric rather than slipping into
      // a portal re-login (B2C). Biometric is only a re-login shortcut when
      // there's no valid session, not an every-launch lock.
      if ((await validateBearerToken()) && (await isPortalTokenValid())) {
        window.location.href = "/AuthCallback";
        return;
      }
      // No valid session: offer biometric as a choice alongside the email form.
      const { available, label } = await isBiometricAvailable();
      const enabled = await isBiometricEnabled();
      if (available && enabled) {
        // Only offer biometric if a stored credential actually exists. If the
        // opt-in flag is set but the credential is gone (e.g., the app was
        // reinstalled during development, or the secure store was cleared),
        // self-heal by turning the opt-in off so we don't dead-end on a button
        // that can't restore anything - fall through to the normal login.
        const credential = await getBiometricCredential();
        if (credential) {
          setBioLabel(label);
          setShowBiometric(true);
          return;
        }
        await setBiometricEnabled(false);
      }
      // Defensive: never leave a login-cached credential in the secure store for
      // a user who is not enrolled in biometric. Credentials are cached at every
      // login for first-attempt reliability; this sweeps up any that belong to a
      // non-enrolled user (no-op if they are enrolled).
      await clearCachedCredentialIfNotEnrolled();
      console.log('Entered authback after logout');
      window.location.href = "/AuthCallback";
    })
  }

  const handleBiometricSignIn = async () => {
    setShowLoading(true);
    const verified = await promptBiometric(`Sign in with ${bioLabel}`);
    if (verified && (await restoreSessionFromBiometric())) {
      // Session restored - tell AuthCallback to skip the B2C round-trips + plant/DB.
      sessionStorage.setItem('biometricRestored', 'true');
      window.location.href = "/AuthCallback";
      return;
    }
    // Biometric failed/cancelled or session not restorable - fall back to email.
    setShowLoading(false);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>EAM Mobile</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        message={`${translatedMessages["ValUser"]?.MessageText || "Validating user"
          }...`}
        duration={15000}
      />
      <IonContent className="ion-padding">
        <IonGrid>
          <IonRow className="ion-justify-content-center">
            <IonCol size="12" size-md="6">
              {showBiometric && (
                <>
                  <IonButton
                    expand="block"
                    onClick={() => handleBiometricSignIn()}
                    style={{ marginBottom: '10px' }}
                  >
                    {`${translations["lbl_signin_with"] || "Sign in with"} ${bioLabel}`}
                  </IonButton>
                  <div style={{ textAlign: 'center', margin: '10px 0', color: '#888' }}>
                    {translations["lbl_or"] || "or"}
                  </div>
                </>
              )}
              <form onSubmit={handleSubmit}>
                <IonInput
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onIonChange={(ev) => {
                    setEmail(ev.detail.value || '');
                  }}
                  style={{ marginBottom: '20px' }}
                />
                <IonButton expand="block" type="submit">{translations["lbl_btn_next"] || "Next"}</IonButton>
              </form>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonPage>

  );
};

export default EmailForm;
